import React, { forwardRef } from 'react'
import styled from 'styled-components'

import Dialog from '@mui/material/Dialog'

import KeyboardAccessible from 'components/KeyboardAccessible'

import { color } from 'styles/colors'
import { screenSize } from 'styles/shared'

type Ref = HTMLDivElement

type Props = React.PropsWithChildren<{
  closeCallback: () => void
  showCloseButton?: boolean
  withHeaderBar?: boolean
  title?: string | null
  subTitle?: string | null
  isDisplayed?: boolean
  variant?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false
  className?: string
  fullWidth?: boolean
  fullScreen?: boolean
  scroll?: 'body' | 'paper'
  dataTestID?: string
  paperStyles?: React.CSSProperties
}>

const Modal = forwardRef<Ref, Props>(({
  title = null,
  subTitle = null,
  showCloseButton = true,
  withHeaderBar = false,
  children,
  closeCallback,
  isDisplayed = false,
  fullWidth = true,
  fullScreen = false,
  variant = 'md',
  className = '',
  scroll = 'body',
  dataTestID = '',
  paperStyles = {}
}: Props, ref) => {
  return (
    <S.Dialog
      ref={ref}
      open={isDisplayed}
      onClose={closeCallback}
      scroll={scroll}
      fullScreen={fullScreen}
      fullWidth={fullWidth}
      maxWidth={variant}
      PaperProps={{
        sx: {
          borderRadius: '32px',
          ...paperStyles
        }
      }}
      aria-labelledby="scroll-dialog-title"
      aria-describedby="scroll-dialog-description"
      disableRestoreFocus
    >
      <S.Content {...{ className }}>
        {
          withHeaderBar && showCloseButton && (
            <S.HeaderBar>
              <S.HeaderCloseButton onClick={closeCallback}>
                <S.HeaderCloseIcon role="button" aria-label="Close" src="/img/close.svg" data-testid={dataTestID} />
              </S.HeaderCloseButton>
            </S.HeaderBar>
          )
        }
        {
          !withHeaderBar && showCloseButton && (
            <S.CloseButton onClick={closeCallback}>
              <S.CloseIcon role="button" aria-label="Close" src="/img/close.svg" data-testid={dataTestID} />
            </S.CloseButton>
          )
        }
        {title && <S.Title>{title}</S.Title> }
        {subTitle && <S.SubTitle>{subTitle}</S.SubTitle> }
        {children}
      </S.Content>
    </S.Dialog>
  )
})

export default Modal

const S = {
  Dialog: styled(Dialog)<{fullScreen: boolean}>`
    ${({ fullScreen }) => fullScreen && `
      .MuiDialog-paper {
        border-radius: 0px;
      }
    `}

    @media ${screenSize.small} {
      margin: 25px;
      max-width: 100%;

      .MuiDialog-paper {
        border-radius: 32px;
        margin: 32px 0px;
        width: 100%;
        max-width: 100%;
      }
      .MuiDialog-paperScrollBody{
        margin: 32px 0px;
        width: 100%;
        max-width: 100%;
      }
    }
  `,
  Title: styled.h1`
    color: ${color('primaryText')};
    font-size: 24px;
    font-weight: 600;
    text-transform: capitalize;
    margin: 0 0 24px 0;

    @media ${screenSize.medium} {
      font-size: 20px;
    }
  `,
  Content: styled.div`
    padding: 24px;
    background: ${color('paneBackground')};

    @media ${screenSize.small} {
      padding: 24px 12px;
    }
  `,
  HeaderBar: styled.div`
    margin: -25px -25px 20px -25px;
    padding: 15px 20px;
    background: ${color('ancillaryInfoHeaderBackground')};
    display: flex;
    justify-content: flex-end;
    align-items: center;
  `,
  HeaderCloseButton: styled(KeyboardAccessible)`
    padding: 5px;
    height: 30px;
    display: flex;
    align-items: center;
  `,
  HeaderCloseIcon: styled.img`
    height: 25px;
    width: 25px;
    cursor: pointer;
  `,
  CloseButton: styled(KeyboardAccessible)`
    float: right;
    padding: 10px;
    margin: -10px;
    height: 30px;
  `,
  CloseIcon: styled.img`
    height: 25px;
    width: 25px;
    margin: -10px -10px 5px -5px;
    cursor: pointer;

    @media ${screenSize.small} {
      margin: -5px 0px 5px -5px;
    }
  `,
  SubTitle: styled.h2`
    color: ${color('primaryText')};
    font-size: 16px;
    font-weight: 400;
    text-transform: capitalize;
    margin-top: -15px;
  `
}
